import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Avatar from "@material-ui/core/Avatar";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SaveIcon from "@material-ui/icons/Save";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

function defaultDateRange(days = 21) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return {
    fromIso: from.toISOString().slice(0, 10),
    toIso: to.toISOString().slice(0, 10),
  };
}

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    direction: "rtl",
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
    alignItems: "end",
    marginBottom: theme.spacing(2),
  },
  field: {
    minWidth: 0,
    width: "100%",
  },
  modelField: {
    minWidth: 0,
    width: "100%",
  },
  turnsField: {
    minWidth: 0,
    width: "100%",
  },
  uploadControl: {
    display: "flex",
    alignItems: "flex-end",
  },
  promptField: {
    marginTop: theme.spacing(1),
  },
  hiddenInput: {
    display: "none",
  },
  fileName: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  errorMsg: {
    color: "#721c24",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  warningMsg: {
    color: "#856404",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  successMsg: {
    color: "#155724",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  summaryCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
  },
  summaryNumber: {
    fontSize: "1.7rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  summaryLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  markdown: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    fontSize: "0.95rem",
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tableContainer: {
    maxHeight: 520,
    marginTop: theme.spacing(2),
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
  },
  productCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 240,
  },
  productName: {
    fontSize: "0.88rem",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  productMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
    marginTop: 2,
  },
  avatar: {
    width: 44,
    height: 44,
    flexShrink: 0,
    backgroundColor: theme.palette.grey[200],
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  traceRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: `${theme.spacing(0.75)}px 0`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: "0.9rem",
  },
});

class OrderTopUpManager extends Component {
  state = {
    models: [],
    defaultModel: "",
    model: "",
    fileName: "",
    fileData: null,
    salesFrom: defaultDateRange().fromIso,
    salesTo: defaultDateRange().toIso,
    includeNewCandidates: true,
    maxTurns: 8,
    systemPrompt: "",
    defaultSystemPrompt: "",
    promptDirty: false,
    promptMessage: null,
    preview: null,
    result: null,
    loadingModels: false,
    loadingPrompt: false,
    savingPrompt: false,
    loadingPreview: false,
    loadingAnalyze: false,
    error: null,
  };

  componentDidMount() {
    this.loadModels();
    this.loadSystemPrompt();
  }

  loadModels = async () => {
    this.setState({ loadingModels: true });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/order-top-up/models`, {
        headers: {
          ...utils.getAdminAuthHeaders(),
        },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load models");
      }
      const models = data.models || [];
      const compatibleModels = models.filter((m) => m.isAgentCompatible);
      const preferred = data.defaultModel || "";
      this.setState({
        models,
        defaultModel: preferred,
        model: compatibleModels.some((m) => m.id === preferred)
          ? preferred
          : compatibleModels[0]?.id || preferred,
        loadingModels: false,
      });
    } catch (err) {
      this.setState({
        loadingModels: false,
        error: err.message,
        models: [],
        model: "",
      });
    }
  };

  loadSystemPrompt = async () => {
    this.setState({ loadingPrompt: true });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/order-top-up/system-prompt`,
        {
          headers: {
            ...utils.getAdminAuthHeaders(),
          },
        }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load system prompt");
      }
      this.setState({
        systemPrompt: data.systemPrompt || "",
        defaultSystemPrompt: data.defaultSystemPrompt || "",
        promptDirty: false,
        loadingPrompt: false,
      });
    } catch (err) {
      this.setState({
        loadingPrompt: false,
        error: err.message,
      });
    }
  };

  saveSystemPrompt = async () => {
    this.setState({ savingPrompt: true, promptMessage: null, error: null });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/order-top-up/system-prompt`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
          body: JSON.stringify({ systemPrompt: this.state.systemPrompt }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to save system prompt");
      }
      this.setState({
        systemPrompt: data.systemPrompt,
        defaultSystemPrompt: data.defaultSystemPrompt || "",
        promptDirty: false,
        savingPrompt: false,
        promptMessage: "ה-system prompt נשמר",
      });
    } catch (err) {
      this.setState({ savingPrompt: false, error: err.message });
    }
  };

  resetSystemPrompt = async () => {
    this.setState({ savingPrompt: true, promptMessage: null, error: null });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/order-top-up/system-prompt/reset`,
        {
          method: "POST",
          headers: {
            ...utils.getAdminAuthHeaders(),
          },
        }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to reset system prompt");
      }
      this.setState({
        systemPrompt: data.systemPrompt,
        defaultSystemPrompt: data.defaultSystemPrompt || "",
        promptDirty: false,
        savingPrompt: false,
        promptMessage: "ה-system prompt אופס לברירת המחדל",
      });
    } catch (err) {
      this.setState({ savingPrompt: false, error: err.message });
    }
  };

  readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    this.setState({
      error: null,
      preview: null,
      result: null,
      loadingPreview: true,
      fileName: file.name,
    });

    try {
      const fileData = await this.readFileAsDataUrl(file);
      this.setState({ fileData });

      const res = await fetch(`${basePath}/api/v1/admin/order-top-up/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({ fileData }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Preview failed");
      }
      this.setState({ preview: data, loadingPreview: false });
    } catch (err) {
      this.setState({
        loadingPreview: false,
        fileData: null,
        error: err.message,
      });
    } finally {
      event.target.value = "";
    }
  };

  handleAnalyze = async ({ skipAi = false } = {}) => {
    const {
      fileData,
      model,
      salesFrom,
      salesTo,
      includeNewCandidates,
      maxTurns,
      systemPrompt,
      promptDirty,
    } = this.state;
    if (!fileData) {
      this.setState({ error: "יש להעלות קובץ Excel לפני הניתוח" });
      return;
    }

    this.setState({ loadingAnalyze: true, error: null, result: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/order-top-up/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          fileData,
          model,
          salesFrom,
          salesTo,
          includeNewCandidates,
          skipAi,
          maxTurns,
          // Use current textarea for this run even if unsaved
          systemPromptOverride: promptDirty ? systemPrompt : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Analysis failed");
      }
      this.setState({ result: data, loadingAnalyze: false });
    } catch (err) {
      this.setState({ loadingAnalyze: false, error: err.message });
    }
  };

  renderPriorityChip(priority) {
    const color =
      priority === "high"
        ? "secondary"
        : priority === "medium"
          ? "primary"
          : "default";
    return <Chip size="small" color={color} label={priority || "—"} />;
  }

  renderProductCell(line) {
    const { classes } = this.props;
    const label = line.name || "מוצר לא נמצא בקטלוג";
    const meta = [line.brand, line.size, line.ean].filter(Boolean).join(" · ");

    return (
      <div className={classes.productCell}>
        <Avatar
          src={line.imageUrl || undefined}
          alt={label}
          className={classes.avatar}
          variant="rounded"
        >
          {!line.imageUrl && (line.brand || label || "?").charAt(0)}
        </Avatar>
        <div>
          <div className={classes.productName}>{label}</div>
          <div className={classes.productMeta}>{meta || "—"}</div>
        </div>
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const {
      models,
      model,
      fileName,
      salesFrom,
      salesTo,
      includeNewCandidates,
      maxTurns,
      systemPrompt,
      promptDirty,
      promptMessage,
      preview,
      result,
      loadingModels,
      loadingPrompt,
      savingPrompt,
      loadingPreview,
      loadingAnalyze,
      error,
    } = this.state;

    const busy = loadingPreview || loadingAnalyze || savingPrompt;

    return (
      <div className={classes.root}>
        <Paper className={classes.section} elevation={1}>
          <Typography variant="h5" gutterBottom>
            בדיקת השלמת הזמנה (Top-up)
          </Typography>
          <Typography className={classes.subtitle}>
            העלה את קובץ ה-Excel של ההזמנה שכבר בוצעה אצל הספק. הסוכן מתייחס לכל
            השורות ככמויות שכבר הוזמנו, שולף נתונים אנליטיים בכמה סיבובים, וממליץ
            רק על תוספות כמות / פריטים חדשים.
          </Typography>

          <div className={classes.controls}>
            <label className={classes.uploadControl}>
              <input
                accept=".xlsx,.xls"
                className={classes.hiddenInput}
                type="file"
                onChange={this.handleFileChange}
                disabled={busy}
              />
              <Button
                variant="outlined"
                color="primary"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={busy}
              >
                העלה Excel מהגיליון
              </Button>
            </label>

            <TextField
              select
              label="מודל OpenAI"
              className={classes.modelField}
              value={model}
              onChange={(e) => this.setState({ model: e.target.value })}
              disabled={loadingModels || busy}
              helperText={
                loadingModels
                  ? "טוען מודלים מ-OpenAI..."
                  : `${models.length} מודלי GPT-5.5 / GPT-5.6 מה-API · ${
                      models.filter((item) => item.isAgentCompatible).length
                    } מתאימים לסוכן`
              }
              SelectProps={{
                MenuProps: {
                  PaperProps: { style: { maxHeight: 360 } },
                },
              }}
            >
              {models.map((modelOption) => (
                <MenuItem
                  key={modelOption.id}
                  value={modelOption.id}
                  disabled={!modelOption.isAgentCompatible}
                >
                  {modelOption.id}
                  {!modelOption.isAgentCompatible
                    ? " — לא תומך Tool Calling"
                    : ""}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="מכירות מ-"
              type="date"
              className={classes.field}
              value={salesFrom}
              onChange={(e) => this.setState({ salesFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={busy}
            />
            <TextField
              label="מכירות עד"
              type="date"
              className={classes.field}
              value={salesTo}
              onChange={(e) => this.setState({ salesTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={busy}
            />

            <TextField
              label="סיבובי סוכן"
              type="number"
              className={classes.turnsField}
              value={maxTurns}
              onChange={(e) =>
                this.setState({
                  maxTurns: Math.min(
                    16,
                    Math.max(1, parseInt(e.target.value, 10) || 1)
                  ),
                })
              }
              inputProps={{ min: 1, max: 16 }}
              helperText="1–16"
              disabled={busy}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeNewCandidates}
                  onChange={(e) =>
                    this.setState({ includeNewCandidates: e.target.checked })
                  }
                  color="primary"
                  disabled={busy}
                />
              }
              label="כלול מועמדים חדשים שלא בהזמנה"
            />
          </div>

          {fileName ? (
            <Typography className={classes.fileName}>קובץ: {fileName}</Typography>
          ) : null}

          {preview ? (
            <Typography className={classes.fileName}>
              זוהו עמודות: EAN = <strong>{preview.eanColumn}</strong>, כמות ={" "}
              <strong>{preview.qtyColumn}</strong> · {preview.uniqueEans} מוצרים
              ייחודיים · {preview.totalOrderedUnits} יחידות שהוזמנו
            </Typography>
          ) : null}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                loadingAnalyze ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => this.handleAnalyze({ skipAi: false })}
              disabled={!this.state.fileData || !model || busy}
            >
              נתח עם סוכן AI
            </Button>
            <Button
              variant="outlined"
              onClick={() => this.handleAnalyze({ skipAi: true })}
              disabled={!this.state.fileData || busy}
            >
              חישוב בלבד (בלי AI)
            </Button>
          </div>

          {error ? <div className={classes.errorMsg}>{error}</div> : null}
          {result?.aiError ? (
            <div className={classes.warningMsg}>
              ניתוח הנתונים הצליח, אך הסוכן נכשל: {result.aiError}. מוצגות
              המלצות היוריסטיות.
            </div>
          ) : null}
        </Paper>

        <Paper className={classes.section} elevation={1}>
          <Typography variant="h6" gutterBottom>
            System prompt לסוכן
          </Typography>
          <Typography className={classes.subtitle}>
            נשמר בשרת ומשמש בכל הריצות. שינויים לא שמורים עדיין ישמשו רק בריצה
            הנוכחית.
            {promptDirty ? " (יש שינויים לא שמורים)" : ""}
          </Typography>
          <TextField
            className={classes.promptField}
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            value={systemPrompt}
            onChange={(e) =>
              this.setState({
                systemPrompt: e.target.value,
                promptDirty: true,
                promptMessage: null,
              })
            }
            disabled={loadingPrompt || busy}
          />
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                savingPrompt ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={this.saveSystemPrompt}
              disabled={loadingPrompt || busy || !systemPrompt.trim()}
            >
              שמור prompt
            </Button>
            <Button
              variant="outlined"
              onClick={this.resetSystemPrompt}
              disabled={loadingPrompt || busy}
            >
              אפס לברירת מחדל
            </Button>
          </div>
          {promptMessage ? (
            <div className={classes.successMsg}>{promptMessage}</div>
          ) : null}
        </Paper>

        {result ? (
          <>
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              {[
                {
                  label: "מוצרים בהזמנה",
                  value: result.summary?.uniqueEans,
                },
                {
                  label: "שורות להשלמה",
                  value: result.summary?.linesNeedingTopUp,
                },
                {
                  label: "יחידות מומלצות להוספה",
                  value: result.summary?.unitsToAdd,
                },
                {
                  label: "סיבובי סוכן",
                  value: result.summary?.agentTurns ?? 0,
                },
              ].map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                  <Paper className={classes.summaryCard} elevation={1}>
                    <div className={classes.summaryNumber}>
                      {card.value ?? 0}
                    </div>
                    <div className={classes.summaryLabel}>{card.label}</div>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {result.agentTrace?.length ? (
              <Paper className={classes.section} elevation={1}>
                <Typography variant="h6" gutterBottom>
                  מהלך הסוכן ({result.summary?.agentTurns || 0} סיבובים)
                </Typography>
                {result.agentTrace.map((step, index) => (
                  <div className={classes.traceRow} key={`${step.turn}-${index}`}>
                    <Chip size="small" label={`#${step.turn}`} />
                    <Chip
                      size="small"
                      color={step.type === "tool" ? "primary" : "default"}
                      label={step.tool || step.type}
                    />
                    <span>{step.summary}</span>
                  </div>
                ))}
              </Paper>
            ) : null}

            {result.ai?.summaryMarkdown ? (
              <Paper className={classes.section} elevation={1}>
                <Typography variant="h6" gutterBottom>
                  סיכום AI ({result.model})
                </Typography>
                <Typography className={classes.markdown}>
                  {result.ai.summaryMarkdown}
                </Typography>
                {result.ai.highlights?.length ? (
                  <div className={classes.chipRow}>
                    {result.ai.highlights.map((h) => (
                      <Chip key={h} label={h} size="small" color="primary" />
                    ))}
                  </div>
                ) : null}
                {result.ai.warnings?.length ? (
                  <div className={classes.chipRow}>
                    {result.ai.warnings.map((w) => (
                      <Chip key={w} label={w} size="small" color="secondary" />
                    ))}
                  </div>
                ) : null}
              </Paper>
            ) : null}

            <Paper className={classes.section} elevation={1}>
              <Typography variant="h6" gutterBottom>
                תוספות מומלצות לשורות קיימות
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>מוצר</TableCell>
                      <TableCell align="right">הוזמן</TableCell>
                      <TableCell align="right">מלאי</TableCell>
                      <TableCell align="right">מכירות בחלון</TableCell>
                      <TableCell align="right">כיסוי</TableCell>
                      <TableCell align="right">המלצה</TableCell>
                      <TableCell>עדיפות</TableCell>
                      <TableCell>סיבות</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(result.topUpLines || []).map((line) => (
                      <TableRow key={line.ean}>
                        <TableCell>{this.renderProductCell(line)}</TableCell>
                        <TableCell align="right">{line.orderedQty}</TableCell>
                        <TableCell align="right">{line.currentStock}</TableCell>
                        <TableCell align="right">
                          {line.recentUnitsSold}
                        </TableCell>
                        <TableCell align="right">{line.coverage}</TableCell>
                        <TableCell align="right">
                          <strong>+{line.finalSuggestedAdd}</strong>
                        </TableCell>
                        <TableCell>
                          {this.renderPriorityChip(
                            line.aiPriority || line.urgency
                          )}
                        </TableCell>
                        <TableCell>
                          {(line.aiReasons || []).join(" · ") ||
                            (line.suggestedAdd > 0
                              ? "פער מול ביקוש/מכירות אחרונות"
                              : "")}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!result.topUpLines?.length ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          לא נמצאו שורות שדורשות תוספת כמות
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {result.newCandidates?.length ? (
              <Paper className={classes.section} elevation={1}>
                <Typography variant="h6" gutterBottom>
                  מועמדים חדשים (לא בהזמנה)
                </Typography>
                <TableContainer className={classes.tableContainer}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>מוצר</TableCell>
                        <TableCell align="right">מלאי</TableCell>
                        <TableCell align="right">YTD</TableCell>
                        <TableCell align="right">מומלץ לרכישה</TableCell>
                        <TableCell align="right">תוספת מומלצת</TableCell>
                        <TableCell>עדיפות</TableCell>
                        <TableCell>סיבות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.newCandidates.map((line) => (
                        <TableRow key={line.ean}>
                          <TableCell>{this.renderProductCell(line)}</TableCell>
                          <TableCell align="right">
                            {line.currentStock}
                          </TableCell>
                          <TableCell align="right">{line.ytdUnits}</TableCell>
                          <TableCell align="right">
                            {line.recommendedPurchase}
                          </TableCell>
                          <TableCell align="right">
                            {line.finalSuggestedAdd != null
                              ? `+${line.finalSuggestedAdd}`
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {this.renderPriorityChip(line.aiPriority)}
                          </TableCell>
                          <TableCell>
                            {(line.aiReasons || []).join(" · ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }
}

export default withStyles(styles)(OrderTopUpManager);
